import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { IRequestsConfig, IResponse, IUserFacingMessages, TLanguages, TRequestType, TSpecificUserFacingMessages, TStatusCode } from './Interfaces'
import UserFacingMessagesEN from './UserFacingMessages/UserFacingMessagesEN'
import UserFacingMessagesLV from './UserFacingMessages/UserFacingMessagesLV'
import { is2XX, is4XX, is5XX, isAxiosError } from './utils'

export default class Requests<TMessageId> {
  lang: TLanguages
  specificUserFacingMessages: TSpecificUserFacingMessages | null
  nextRequestMsgId: TMessageId | null = null
  constructor (config: IRequestsConfig) {
    this.lang = config.language
    this.specificUserFacingMessages = config.specificUserFacingMessages ?? null
  }

  async get <TData>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<IResponse<TData>> {
    return this._doRequest<TData>('GET', url, null, config)
  }

  async post <TBody, TReturnData>(
    url: string,
    body: TBody,
    config?: AxiosRequestConfig
  ): Promise<IResponse<TReturnData>> {
    return this._doRequest<TReturnData>('POST', url, body, config)
  }

  async put <TBody, TReturnData>(
    url: string,
    body: TBody,
    config?: AxiosRequestConfig
  ): Promise<IResponse<TReturnData>> {
    return this._doRequest<TReturnData>('PUT', url, body, config)
  }

  async delete <TReturnData>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<IResponse<TReturnData>> {
    return this._doRequest<TReturnData>('DELETE', url, null, config)
  }

  setNextRequestMsgId (id: TMessageId) {
    this.nextRequestMsgId = id
  }

  async _doRequest <TData>(
    reqType: TRequestType,
    url: string,
    body?: any,
    config?: AxiosRequestConfig
  ): Promise<IResponse<TData>> {
    let fmtdRes

    try {
      let res
      switch (reqType) {
        case 'GET':
          res = await axios.get<TData>(url, config)
          break
        case 'POST':
          res = await axios.post<TData>(url, body, config)
          break
        case 'PUT':
          res = await axios.put<TData>(url, body, config)
          break
        case 'DELETE':
          res = await axios.delete<TData>(url, config)
          break
      }

      fmtdRes = this._processResponse<TData>(res)
    } catch (err) {
      fmtdRes = this._processRequestError<TData>(err)
    }

    this._doPostReqCleanUp()
    return fmtdRes
  }

  _doPostReqCleanUp () {
    this.nextRequestMsgId = null
  }

  _processResponse <T>(res: AxiosResponse<T>): IResponse<T> {
    if (is2XX(res.status)) {
      return this._process2XXResponse<T>(res)
    }

    // Can add different non-4XX/-5XX status handling here (e.g. 3XX)
    // ...

    // Somehow status is something different? Treat it as 2XX for now.
    return this._process2XXResponse<T>(res)
  }
  _processRequestError <T>(err: any): IResponse<T> {
    if (isAxiosError(err)) {
      if (err.response) {
        if (is4XX(err.response.status)) {
          return this._process4XXError<T>(err)
        }
        if (is5XX(err.response.status)) {
          return this._process5XXError<T>(err)
        }

        // Status somehow missing? System-side fault (400).
        return this._getDefault4XXFormattedResponse<T>()
      } else {
        // No response of any kind? System-side fault (400).
        return this._getDefault4XXFormattedResponse<T>()
      }
    } else {
      // Not an axios error? System-side fault (400).
      return this._getDefault4XXFormattedResponse<T>()
    }
  }

  _process2XXResponse <T>(res: AxiosResponse<T>): IResponse<T> {
    return {
      ok: true,
      userFacingMsg: this._getUserFacingMessage(res.status, 'ok'),
      data: res.data,
      statusCode: res.status
    }
  }
  _process4XXError <T>(err: AxiosError<T>): IResponse<T> {
    return {
      ok: false,
      userFacingMsg: this._getUserFacingMessage(
        err.response?.status ?? 400,
        'request-fail'
      ),
      data: err.response?.data ?? null,
      statusCode: err.response?.status ?? 400
    }
  }
  _process5XXError <T>(err: AxiosError<T>): IResponse<T> {
    return {
      ok: false,
      userFacingMsg: this._getUserFacingMessage(
        err.response?.status ?? 500,
        'server-fail'
      ),
      data: err.response?.data ?? null,
      statusCode: err.response?.status ?? 500
    }
  }

  _getDefault4XXFormattedResponse <T>(): IResponse<T> {
    return {
      ok: false,
      userFacingMsg: this._getUserFacingMessage(400, 'request-fail'),
      data: null,
      statusCode: 400
    }
  }
  _getDefault5XXFormattedResponse <T>(): IResponse<T> {
    return {
      ok: false,
      userFacingMsg: this._getUserFacingMessage(500, 'server-fail'),
      data: null,
      statusCode: 500
    }
  }

  _getUserFacingMessage (
    statusCode: TStatusCode,
    msgKey: keyof IUserFacingMessages
  ) {
    return (
      this._getSpecificUserFacingMessage(statusCode)
      ?? this._getDefaultUserFacingMessage(msgKey)
    )
  }

  _getSpecificUserFacingMessage (statusCode: TStatusCode) {
    if (
      this.specificUserFacingMessages == null ||
      this.nextRequestMsgId == null ||
      typeof this.nextRequestMsgId != 'string' ||
      !this.specificUserFacingMessages[ this.lang ]
    ) return null

    const userFacingMessagesInSetLanguage =
      this.specificUserFacingMessages[ this.lang ]
    if (!userFacingMessagesInSetLanguage) return null

    const state = is2XX(statusCode) ? 'ok' : 'fail'
    return userFacingMessagesInSetLanguage[ this.nextRequestMsgId ][ state ]
  }

  /**
   * Populate this function with language objects
   */
  _getDefaultUserFacingMessage (msgKey: keyof IUserFacingMessages): string {
    switch (this.lang) {
      case 'en':
        return UserFacingMessagesEN[ msgKey ]
      case 'lv':
        return UserFacingMessagesLV[ msgKey ]
    }
  }
}
